import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiStatus, fetchInvestJob, type InvestJob, type InvestJobName } from './api';

interface InvestLongJobOptions {
  name: InvestJobName;
  start: () => Promise<InvestJob>;
  onReady: (job: InvestJob) => void | Promise<void>;
}

function runningFallback(name: InvestJobName): InvestJob {
  return {
    nome: name,
    stato: 'in_corso',
    iniziato: new Date().toISOString(),
    finito: null,
    messaggio: null,
    risultato: null,
  };
}

export function useInvestLongJob({ name, start, onReady }: InvestLongJobOptions) {
  const queryClient = useQueryClient();
  const handledReady = useRef<string | null>(null);
  const queryKey = ['invest-job', name] as const;
  const job = useQuery({
    queryKey,
    queryFn: () => fetchInvestJob(name),
    refetchInterval: (current) => current.state.data?.stato === 'in_corso' ? 10_000 : false,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      const status = apiStatus(error);
      return status != null && status >= 500 && failureCount < 1;
    },
  });
  const launch = useMutation({
    mutationFn: async () => {
      try {
        return await start();
      } catch (error) {
        if (apiStatus(error) !== 409) throw error;
        return await fetchInvestJob(name) ?? runningFallback(name);
      }
    },
    onSuccess: (result) => queryClient.setQueryData(queryKey, result),
  });

  useEffect(() => {
    const current = job.data;
    if (current?.stato !== 'pronto') return;
    const readyKey = `${current.finito ?? ''}:${current.risultato ?? ''}`;
    if (handledReady.current === readyKey) return;
    handledReady.current = readyKey;
    void Promise.resolve(onReady(current)).catch(() => undefined);
  }, [job.data, onReady]);

  const stateError = job.data?.stato === 'errore'
    ? new Error(job.data.messaggio || 'Il lavoro non è stato completato.')
    : null;

  return {
    start: () => {
      launch.reset();
      launch.mutate();
    },
    isChecking: job.isPending,
    isRunning: launch.isPending || job.data?.stato === 'in_corso',
    error: launch.isPending ? null : launch.error ?? job.error ?? stateError,
  };
}
